import ColorThief from 'colorthief';
import type {RGBA} from '@Utils/color.util';

const DEFAULT_MIN_PIXEL_FRACTION = 0.05;
const PALETTE_CANDIDATE_COUNT = 20;
const SAMPLE_CANVAS_SIZE = 100;
const COLOR_THIEF_QUALITY = 10;
const IDLE_TIMEOUT_MS = 1500;
const MIN_ALPHA = 125;
const BUCKET_SHIFT = 3;

// Constructed lazily: colorthief's node build has no constructible default export, so a
// module-scope `new` breaks every test that transitively imports this file.
let colorThief: ColorThief | null = null;

const getColorThief = (): ColorThief => {
  colorThief ??= new ColorThief();
  return colorThief;
};

interface PixelBucket {
  count: number;
  sumR: number;
  sumG: number;
  sumB: number;
}

interface ClusterStats {
  count: number;
  buckets: Map<number, PixelBucket>;
}

const sampleSourceToCanvas = (source: CanvasImageSource, sourceWidth: number, sourceHeight: number): HTMLCanvasElement | null => {
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_CANVAS_SIZE;
  canvas.height = SAMPLE_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, SAMPLE_CANVAS_SIZE, SAMPLE_CANVAS_SIZE);
  return canvas;
};

/**
 * Single-pass cluster assignment + modal-bucket histogram. For each qualifying pixel we find
 * its nearest centroid (Euclidean RGB), bump that cluster's pixel count, and drop the pixel
 * into an 8-unit modal-bucket histogram for that cluster. Clusters whose pixel share is below
 * `minPixelFraction` are dropped; surviving clusters emit the mean of their modal bucket — the
 * tightest pixel cloud inside the cluster — which avoids the sRGB-averaging darkening of plain
 * centroids. Results are returned ordered by descending cluster size.
 */
const extractFromCanvas = (canvas: HTMLCanvasElement, minPixelFraction: number): RGBA[] => {
  const palette = getColorThief().getPalette(canvas, {colorCount: PALETTE_CANDIDATE_COUNT, quality: COLOR_THIEF_QUALITY, ignoreWhite: false});
  if (!palette || palette.length === 0) {
    return [];
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return [];
  }
  const {data} = ctx.getImageData(0, 0, canvas.width, canvas.height);

  const stats: ClusterStats[] = palette.map(() => {
    return {count: 0, buckets: new Map<number, PixelBucket>()};
  });
  let totalQualifying = 0;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < MIN_ALPHA) {
      continue;
    }
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let k = 0; k < palette.length; k += 1) {
      const dr = r - palette[k][0];
      const dg = g - palette[k][1];
      const db = b - palette[k][2];
      const dist = dr * dr + dg * dg + db * db;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = k;
      }
    }

    const stat = stats[bestIdx];
    stat.count += 1;
    totalQualifying += 1;

    const key = ((r >> BUCKET_SHIFT) << 16) | ((g >> BUCKET_SHIFT) << 8) | (b >> BUCKET_SHIFT);
    const existing = stat.buckets.get(key);
    if (existing) {
      existing.count += 1;
      existing.sumR += r;
      existing.sumG += g;
      existing.sumB += b;
    } else {
      stat.buckets.set(key, {count: 1, sumR: r, sumG: g, sumB: b});
    }
  }

  if (totalQualifying === 0) {
    return [];
  }

  const minCount = totalQualifying * minPixelFraction;
  const survivors: {count: number; color: RGBA}[] = [];
  for (const stat of stats) {
    if (stat.count < minCount) {
      continue;
    }
    let best: PixelBucket | undefined;
    for (const bucket of stat.buckets.values()) {
      if (best === undefined || bucket.count > best.count) {
        best = bucket;
      }
    }
    if (best !== undefined) {
      survivors.push({
        count: stat.count,
        color: [Math.round(best.sumR / best.count), Math.round(best.sumG / best.count), Math.round(best.sumB / best.count), 1],
      });
    }
  }
  survivors.sort((a, b) => b.count - a.count);
  return survivors.map((s) => s.color);
};

export const extractDominantColorsFromImage = (img: HTMLImageElement, minPixelFraction: number = DEFAULT_MIN_PIXEL_FRACTION): RGBA[] => {
  if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
    return [];
  }
  try {
    const canvas = sampleSourceToCanvas(img, img.naturalWidth, img.naturalHeight);
    if (!canvas) {
      return [];
    }
    return extractFromCanvas(canvas, minPixelFraction);
  } catch {
    return [];
  }
};

export const extractDominantColorsFromVideo = (video: HTMLVideoElement, minPixelFraction: number = DEFAULT_MIN_PIXEL_FRACTION): RGBA[] => {
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth === 0 || video.videoHeight === 0) {
    return [];
  }
  try {
    const canvas = sampleSourceToCanvas(video, video.videoWidth, video.videoHeight);
    if (!canvas) {
      return [];
    }
    return extractFromCanvas(canvas, minPixelFraction);
  } catch {
    return [];
  }
};

export const scheduleDominantColorExtraction = (work: () => void): void => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(work, {timeout: IDLE_TIMEOUT_MS});
    return;
  }
  setTimeout(work, 0);
};
